/**
 * AdminPageShell — shared header, nav, and access-guard for all /admin/* pages.
 *
 * Usage:
 *   <AdminPageShell title="Jurisdictions" sysAdminOnly>
 *     <JurisdictionsPanel />
 *   </AdminPageShell>
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useUser } from '@/hooks/useUser';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AdminNav } from './AdminNav';

interface AdminPageShellProps {
  /** Section name shown as "Administration: {title}". */
  title: string;
  /** Redirect non-SYSTEM_ADMIN users back to /admin. */
  sysAdminOnly?: boolean;
  /** Hide the AdminNav tab bar (e.g. on org detail pages). */
  hideNav?: boolean;
  children: React.ReactNode;
}

export const AdminPageShell: React.FC<AdminPageShellProps> = ({
  title,
  sysAdminOnly = false,
  hideNav = false,
  children,
}) => {
  usePageTitle(`Admin: ${title}`);
  const { user, loading } = useUser();

  // Wait for the user profile fetch to resolve before guarding — on a hard
  // refresh `user` is briefly null while loading, which would otherwise
  // bounce the admin straight back to /dashboard.
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'SYSTEM_ADMIN' && user.role !== 'ORG_OWNER')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (sysAdminOnly && user.role !== 'SYSTEM_ADMIN') {
    return <Navigate to="/admin" replace />;
  }

  const isSysAdmin = user.role === 'SYSTEM_ADMIN';

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">
            Administration: {title}
          </h1>
        </div>
        <p className="text-gray-600">
          Manage organizations, tools, engine connections, skills, and
          system-wide settings
        </p>
      </div>

      {!hideNav && <AdminNav isSysAdmin={isSysAdmin} />}

      <div>{children}</div>
    </div>
  );
};
